import { ComponentType } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { canSee, Feature } from './lib/features';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Members from './pages/Members';
import UserGroups from './pages/UserGroups';
import Sync from './pages/Sync';
import Analytics from './pages/Analytics';
import Member360 from './pages/Member360';
import Reports from './pages/Reports';
import CihrReport from './pages/CihrReport';
import WebsiteForms from './pages/WebsiteForms';
import FormFamily from './pages/FormFamily';
import Webinars from './pages/Webinars';
import Messages from './pages/Messages';
import Contacts from './pages/Contacts';
import Programs from './pages/Programs';
import Payments from './pages/Payments';
import Stipends from './pages/Stipends';
import Committees from './pages/Committees';
import Availability from './pages/Availability';
import Events from './pages/Events';
import Checkin from './pages/Checkin';
import FellowReview from './pages/review/FellowReview';
import FellowReviewCycle from './pages/review/FellowReviewCycle';
import ReviewerPortal from './pages/review/ReviewerPortal';
import { LoadingPanel } from './components/ui/States';
// Usage watch (throwaway: backend/src/modules/usage/README.md)
import UsageTracker from './components/usage/UsageTracker';
import Usage from './pages/Usage';

// Listed in landing order: an account starts on the first page it can see.
const PAGES: [Feature, string, ComponentType][] = [
  ['dashboard', '/dashboard', Dashboard],
  ['events', '/events', Events],
  ['events', '/events/:eventId', Events],
  ['members', '/members', Members],
  ['groups', '/groups', UserGroups],
  ['sync', '/sync', Sync],
  ['analytics', '/analytics', Analytics],
  ['member360', '/analytics/members/:userId', Member360],
  ['reports', '/reports', Reports],
  ['cihr', '/reports/cihr', CihrReport],
  ['forms', '/forms', WebsiteForms],
  ['forms', '/forms/:familyKey', FormFamily],
  ['webinars', '/webinars', Webinars],
  ['messages', '/messages', Messages],
  ['contacts', '/contacts', Contacts],
  ['programs', '/programs', Programs],
  ['payments', '/payments', Payments],
  ['stipends', '/stipends', Stipends],
  ['committees', '/committees', Committees],
  ['fellowReview', '/fellow-review', FellowReview],
  ['fellowReview', '/fellow-review/:cycleId', FellowReviewCycle],
];

function App() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (location.pathname === '/review' || location.pathname.startsWith('/review/')) return <ReviewerPortal />;
  if (location.pathname.startsWith('/availability/')) return <Availability />;
  if (location.pathname.startsWith('/checkin/')) return <Checkin />;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingPanel label="Loading platform…" height={200} />
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const pages = PAGES.filter(([feature]) => canSee(user.features, feature));
  const landing = pages[0]?.[1];

  if (!landing) {
    return (
      <Layout>
        <div className="text-table text-ink-2 py-12">
          This account has no pages yet. Ask a platform administrator to grant access.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <UsageTracker />
      <Routes>
        <Route path="/" element={<Navigate to={landing} replace />} />
        {user.role === 'super_admin' ? <Route path="/usage" element={<Usage />} /> : null}
        {canSee(user.features, 'members') ? <Route path="/users" element={<Navigate to="/members" replace />} /> : null}
        {pages.map(([, path, Page]) => (
          <Route key={path} path={path} element={<Page />} />
        ))}
        <Route path="*" element={<Navigate to={landing} replace />} />
      </Routes>
    </Layout>
  );
}

export default App;
